import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { ArrowLeft, Save, Sparkles, BookOpen, Send, Bot, User, X, Loader2, CheckCircle, Trash2 } from 'lucide-react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { auth, db } from '@/firebase/config';
import { doc, setDoc, getDoc, getDocs, collection, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { geminiMultimodalGenerate } from '@/services/geminiServices';
import { useToast } from "@/hooks/use-toast";
import { toast as sonnerToast } from "sonner";

// Import markdown guidelines raw
import cbcContextRaw from './cbc-recomendations.md?raw';
import univContextRaw from './universal-lesson-plan.md?raw';

interface BulletTextareaProps {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const BulletTextarea = ({ value, onChange, placeholder, className, disabled }: BulletTextareaProps) => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const el = e.currentTarget;
      const start = el.selectionStart;
      const end = el.selectionEnd;

      // Check if the current line is an empty bullet — if so, pressing Enter should
      // exit bullet mode (remove the empty bullet) instead of creating a new one.
      const beforeCursor = value.substring(0, start);
      const currentLineStart = beforeCursor.lastIndexOf('\n') + 1;
      const currentLine = beforeCursor.substring(currentLineStart);
      if (currentLine === '• ' || currentLine === '•') {
        const newValue = value.substring(0, currentLineStart) + value.substring(end);
        onChange(newValue);
        setTimeout(() => {
          el.selectionStart = el.selectionEnd = currentLineStart;
        }, 0);
        return;
      }

      const newValue = value.substring(0, start) + "\n• " + value.substring(end);
      onChange(newValue);
      setTimeout(() => {
        el.selectionStart = el.selectionEnd = start + 3;
      }, 0);
      return;
    }

    if (e.key === 'Backspace') {
      const el = e.currentTarget;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      if (start !== end || start === 0) return;
      // If cursor is right after "• " at the start of a line, remove the bullet
      const beforeCursor = value.substring(0, start);
      const currentLineStart = beforeCursor.lastIndexOf('\n') + 1;
      const currentLine = beforeCursor.substring(currentLineStart);
      if (currentLine === '• ' && start === currentLineStart + 2) {
        e.preventDefault();
        const newValue = value.substring(0, currentLineStart) + value.substring(end);
        onChange(newValue);
        setTimeout(() => {
          el.selectionStart = el.selectionEnd = currentLineStart;
        }, 0);
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    let val = e.target.value;
    if (val.trim().length > 0 && !val.startsWith('•')) {
      val = '• ' + val.trimStart();
    }
    const smartCaps = val.replace(/(^|\n• |^• )([a-z])/g, (_match, p1, p2) => p1 + p2.toUpperCase());
    onChange(smartCaps);
  };

  return (
    <Textarea
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
    />
  );
};

const LessonCreateView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  useEffect(() => {
    async function fetchLesson() {
      if (!id || !auth.currentUser) return;
      try {
        const docRef = doc(db, 'teaching_workspaces', auth.currentUser.uid, 'lessons', id);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const data = snap.data();
          setTitle(data.title || '');
          setPlanType(data.planType || 'universal');
          setLessonScope(data.lessonScope || 'subject');
          setSelectedTopic(data.selectedTopic || '');
          if (data.planType === 'cbc') {
            setCbcStrand(data.cbcStrand || '');
            setCbcSubStrand(data.cbcSubStrand || '');
            setCbcKiq(data.cbcKiq || '');
            setCbcKnowledge(data.cbcKnowledge || '');
            setCbcSkill(data.cbcSkill || '');
            setCbcAttitude(data.cbcAttitude || '');
            setCbcTextbook(data.cbcTextbook || '');
            setCbcRealia(data.cbcRealia || '');
            setCbcDigital(data.cbcDigital || '');
            setCbcIntro(data.cbcIntro || '');
            setCbcDevelopment(data.cbcDevelopment || '');
            setCbcConclusionPhase(data.cbcConclusionPhase || '');
            setCbcCompetencies(data.cbcCompetencies || '');
            setCbcPci(data.cbcPci || '');
            setCbcValues(data.cbcValues || '');
            setCbcAssessmentMethod(data.cbcAssessmentMethod || '');
            setCbcRubric(data.cbcRubric || '');
            setCbcDifferentiation(data.cbcDifferentiation || '');
            setCbcWhatWentWell(data.cbcWhatWentWell || '');
            setCbcWhatDidnt(data.cbcWhatDidnt || '');
            setCbcFollowUp(data.cbcFollowUp || '');
            setCbcRemarks(data.cbcRemarks || '');
          } else {
            setOutcomes(data.outcomes || '');
            setUnivMaterials(data.univMaterials || '');
            setUnivAnticipatory(data.univAnticipatory || '');
            setUnivIDo(data.univIDo || '');
            setUnivWeDo(data.univWeDo || '');
            setUnivYouDo(data.univYouDo || '');
            setUnivDifferentiation(data.univDifferentiation || '');
            setUnivFormative(data.univFormative || '');
            setUnivSummative(data.univSummative || '');
            setUnivClosure(data.univClosure || '');
            setReflection(data.reflection || '');
          }
        }
      } catch (err) {
        console.error("Failed to load lesson:", err);
      }
    }
    fetchLesson();
  }, [id]);
  const lessonState = location.state as { subjectId?: string; subjectName?: string; syllabus?: { topics?: { id: string; title: string; subTopics?: { id: string; title: string }[] }[] } | null; from?: string } | null;
  const subjectName = lessonState?.subjectName ?? '';
  const syllabusTopics = useMemo(
    () => (Array.isArray(lessonState?.syllabus?.topics) ? lessonState.syllabus!.topics! : []),
    [lessonState?.syllabus]
  );
  const [title, setTitle] = useState('');
  const [outcomes, setOutcomes] = useState('');
  const [reflection, setReflection] = useState('');
  const [planType, setPlanType] = useState<'universal' | 'cbc'>('universal');
  const [lessonScope, setLessonScope] = useState<'subject' | 'topic'>('subject');
  const [selectedTopic, setSelectedTopic] = useState('');
  // CBC-specific state fields
  const [cbcStrand, setCbcStrand] = useState('');
  const [cbcSubStrand, setCbcSubStrand] = useState('');
  const [cbcKiq, setCbcKiq] = useState('');
  const [cbcKnowledge, setCbcKnowledge] = useState('');
  const [cbcSkill, setCbcSkill] = useState('');
  const [cbcAttitude, setCbcAttitude] = useState('');
  const [cbcTextbook, setCbcTextbook] = useState('');
  const [cbcRealia, setCbcRealia] = useState('');
  const [cbcDigital, setCbcDigital] = useState('');
  const [cbcIntro, setCbcIntro] = useState('');
  const [cbcDevelopment, setCbcDevelopment] = useState('');
  const [cbcConclusionPhase, setCbcConclusionPhase] = useState('');
  const [cbcCompetencies, setCbcCompetencies] = useState('');
  const [cbcPci, setCbcPci] = useState('');
  const [cbcValues, setCbcValues] = useState('');
  const [cbcAssessmentMethod, setCbcAssessmentMethod] = useState('');
  const [cbcRubric, setCbcRubric] = useState('');
  const [cbcDifferentiation, setCbcDifferentiation] = useState('');
  const [cbcWhatWentWell, setCbcWhatWentWell] = useState('');
  const [cbcWhatDidnt, setCbcWhatDidnt] = useState('');
  const [cbcFollowUp, setCbcFollowUp] = useState('');
  const [cbcRemarks, setCbcRemarks] = useState('');
  // Universal-specific state fields
  const [univMaterials, setUnivMaterials] = useState('');
  const [univAnticipatory, setUnivAnticipatory] = useState('');
  const [univIDo, setUnivIDo] = useState('');
  const [univWeDo, setUnivWeDo] = useState('');
  const [univYouDo, setUnivYouDo] = useState('');
  const [univDifferentiation, setUnivDifferentiation] = useState('');
  const [univFormative, setUnivFormative] = useState('');
  const [univSummative, setUnivSummative] = useState('');
  const [univClosure, setUnivClosure] = useState('');

  // AI Assist State
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiMessages, setAiMessages] = useState<{role: 'user'|'assistant', content: string}[]>([]);
  const [aiInput, setAiInput] = useState('');
  const [isAiLoading, setIsAiLoading] = useState(false);
  const aiScrollRef = useRef<HTMLDivElement>(null);
  const aiInputRef = useRef<HTMLTextAreaElement>(null);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  // Delete subject state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteSubject = async () => {
    const subjectId = lessonState?.subjectId;
    if (!subjectId || !auth.currentUser) return;
    setIsDeleting(true);
    try {
      const uid = auth.currentUser.uid;
      const courseRef = doc(db, `teaching_workspaces/${uid}/courses`, subjectId);

      // Delete syllabus data subcollection
      const syllabusCol = collection(db, `teaching_workspaces/${uid}/courses/${subjectId}/syllabus_data`);
      const syllabusSnap = await getDocs(syllabusCol);
      const deletePromises = syllabusSnap.docs.map((d) => deleteDoc(d.ref));
      await Promise.all(deletePromises);

      await deleteDoc(courseRef);
      sonnerToast.success("Subject deleted successfully");
      navigate('/dashboard/lessons', { replace: true });
    } catch (err) {
      console.error("Error deleting subject:", err);
      sonnerToast.error("Failed to delete subject");
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
    }
  };

  // Back button auto-hide
  const [backVisible, setBackVisible] = useState(true);
  const backTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showBack = useCallback(() => {
    setBackVisible(true);
    if (backTimer.current !== null) clearTimeout(backTimer.current);
    backTimer.current = setTimeout(() => setBackVisible(false), 5000);
  }, []);

  useEffect(() => {
    showBack();
    const handleMouseMove = () => showBack();
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (backTimer.current !== null) clearTimeout(backTimer.current);
    };
  }, [showBack]);

  // Auto-fill lesson title based on subject, plan type, scope, and selected topic
  useEffect(() => {
    if (!subjectName) return;

    const planLabel = planType === 'cbc' ? 'CBC (KICD)' : 'Universal Standard';

    if (lessonScope === 'subject') {
      setTitle(`${subjectName} — ${planLabel}`);
      return;
    }

    if (!selectedTopic) return;

    const [topicId, subTopicId] = selectedTopic.split('__');
    const topic = syllabusTopics.find((t) => t.id === topicId);
    if (!topic) return;

    if (subTopicId) {
      const sub = topic.subTopics?.find((s) => s.id === subTopicId);
      if (sub) {
        setTitle(`${subjectName} — ${topic.title}: ${sub.title}`);
      }
    } else {
      setTitle(`${subjectName} — ${topic.title}`);
    }
  }, [subjectName, planType, lessonScope, selectedTopic, syllabusTopics]);

  useEffect(() => {
    if (aiScrollRef.current) {
      aiScrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [aiMessages]);

  // Scroll to bottom when mobile keyboard opens/closes
  useEffect(() => {
    const onVpResize = () => {
      setTimeout(() => {
        if (aiScrollRef.current) {
          aiScrollRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 150);
    };
    window.visualViewport?.addEventListener('resize', onVpResize);
    return () => window.visualViewport?.removeEventListener('resize', onVpResize);
  }, []);

  // Lock body scroll when AI panel is open
  useEffect(() => {
    if (aiPanelOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [aiPanelOpen]);

  const handleSave = async () => {
    if (!auth.currentUser) return;
    setIsSaving(true);
    try {
      const uid = auth.currentUser.uid;
      let docId = id; // use route param if editing
      if (!docId) {
        docId = lessonState?.subjectId || `lesson_${Date.now()}`;
        if (lessonScope === 'topic' && selectedTopic) {
          docId = `${docId}_${selectedTopic.replace(/__/g, '_')}`;
        }
      }

      const lessonData = {
        title,
        subjectId: lessonState?.subjectId || null,
        subjectName,
        planType,
        lessonScope,
        selectedTopic,
        updatedAt: serverTimestamp(),
      };

      if (planType === 'cbc') {
        Object.assign(lessonData, {
          cbcStrand, cbcSubStrand, cbcKiq, cbcKnowledge, cbcSkill, cbcAttitude,
          cbcTextbook, cbcRealia, cbcDigital, cbcIntro, cbcDevelopment, cbcConclusionPhase,
          cbcCompetencies, cbcPci, cbcValues, cbcAssessmentMethod, cbcRubric, cbcDifferentiation,
          cbcWhatWentWell, cbcWhatDidnt, cbcFollowUp, cbcRemarks
        });
      } else {
        Object.assign(lessonData, {
          outcomes, univMaterials, univAnticipatory, univIDo, univWeDo, univYouDo,
          univDifferentiation, univFormative, univSummative, univClosure, reflection
        });
      }

      const docRef = doc(db, 'teaching_workspaces', uid, 'lessons', docId);
      await setDoc(docRef, lessonData, { merge: true });
      toast({
        title: "Success",
        description: "Lesson plan saved successfully!",
      });
    } catch (error) {
      console.error('Failed to save lesson plan:', error);
      toast({
        title: "Error",
        description: "Failed to save lesson plan.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendAiMessage = async () => {
    if (!aiInput.trim() || isAiLoading) return;
    const userMsg = { role: 'user' as const, content: aiInput };
    setAiMessages(prev => [...prev, userMsg]);
    setAiInput('');
    setIsAiLoading(true);

    try {
      const historyContext = aiMessages.map(m => `${m.role.toUpperCase()}: ${m.content}`).join('\n');
      const guidelines = planType === 'cbc' ? cbcContextRaw : univContextRaw;
      
      let topicContext = "Not specified";
      if (selectedTopic) {
        const topicObj = syllabusTopics.find((t) => t.id === selectedTopic.split('__')[0]);
        if (topicObj) topicContext = topicObj.title;
      }
      
      const systemPrompt = `You are an AI assistant helping a teacher build a ${planType === 'cbc' ? 'Kenya CBC' : 'Universal Standard'} lesson plan.
The subject is: "${subjectName || 'Not specified'}".
The selected topic is: "${topicContext}".
Do NOT ask the teacher for the subject or topic, as it is already known.

Here are the official guidelines for the required sections:
--- GUIDELINES ---
${guidelines}
--- END GUIDELINES ---

Your task is to ask the teacher questions, 2 or 3 at a time, to gather all necessary details to fill out the form sections. Do NOT ask everything at once. Keep the tone friendly and conversational.
IMPORTANT: Do NOT use markdown asterisks (*) or bold text in your conversational replies. Format your text clearly without asterisks to avoid looking like a mistake.

Once you have gathered enough information for EVERY section of the ${planType} lesson plan, you MUST output a special JSON block at the very end of your final message so the app can auto-fill the form.
The format MUST be exactly like this (fill in the values with the generated content):
%%FORM_DATA%%
{
  ${planType === 'cbc' ? `"cbcStrand": "...", "cbcSubStrand": "...", "cbcKiq": "...", "cbcKnowledge": "...", "cbcSkill": "...", "cbcAttitude": "...", "cbcTextbook": "...", "cbcRealia": "...", "cbcDigital": "...", "cbcIntro": "...", "cbcDevelopment": "...", "cbcConclusionPhase": "...", "cbcCompetencies": "...", "cbcPci": "...", "cbcValues": "...", "cbcAssessmentMethod": "...", "cbcRubric": "...", "cbcDifferentiation": "..."` : `"outcomes": "...", "univMaterials": "...", "univAnticipatory": "...", "univIDo": "...", "univWeDo": "...", "univYouDo": "...", "univDifferentiation": "...", "univFormative": "...", "univSummative": "...", "univClosure": "..."`}
}
%%
IMPORTANT RULE FOR JSON OUTPUT: For any field that represents a list or bullet points (like Outcomes, Materials, Competencies, etc.), you MUST format the string with actual bullet characters (•) and newline characters (\\n). For example: "• First point\\n• Second point".

DO NOT output this JSON block until you have enough info. Until then, just ask questions naturally.
`;

      const fullPrompt = `${systemPrompt}\n\n--- HISTORY ---\n${historyContext}\n\n--- USER SAYS ---\n${userMsg.content}`;
      const response = await geminiMultimodalGenerate(fullPrompt, null, auth.currentUser?.uid);
      
      let cleanResponse = response || '';
      const formMatch = cleanResponse.match(/%%FORM_DATA%%\s*(\{[\s\S]*?\})\s*%%/);
      
      if (formMatch) {
        try {
          const formData = JSON.parse(formMatch[1]);
          // Update the state with parsed data
          if (planType === 'cbc') {
            if (formData.cbcStrand) setCbcStrand(formData.cbcStrand);
            if (formData.cbcSubStrand) setCbcSubStrand(formData.cbcSubStrand);
            if (formData.cbcKiq) setCbcKiq(formData.cbcKiq);
            if (formData.cbcKnowledge) setCbcKnowledge(formData.cbcKnowledge);
            if (formData.cbcSkill) setCbcSkill(formData.cbcSkill);
            if (formData.cbcAttitude) setCbcAttitude(formData.cbcAttitude);
            if (formData.cbcTextbook) setCbcTextbook(formData.cbcTextbook);
            if (formData.cbcRealia) setCbcRealia(formData.cbcRealia);
            if (formData.cbcDigital) setCbcDigital(formData.cbcDigital);
            if (formData.cbcIntro) setCbcIntro(formData.cbcIntro);
            if (formData.cbcDevelopment) setCbcDevelopment(formData.cbcDevelopment);
            if (formData.cbcConclusionPhase) setCbcConclusionPhase(formData.cbcConclusionPhase);
            if (formData.cbcCompetencies) setCbcCompetencies(formData.cbcCompetencies);
            if (formData.cbcPci) setCbcPci(formData.cbcPci);
            if (formData.cbcValues) setCbcValues(formData.cbcValues);
            if (formData.cbcAssessmentMethod) setCbcAssessmentMethod(formData.cbcAssessmentMethod);
            if (formData.cbcRubric) setCbcRubric(formData.cbcRubric);
            if (formData.cbcDifferentiation) setCbcDifferentiation(formData.cbcDifferentiation);
          } else {
            if (formData.outcomes) setOutcomes(formData.outcomes);
            if (formData.univMaterials) setUnivMaterials(formData.univMaterials);
            if (formData.univAnticipatory) setUnivAnticipatory(formData.univAnticipatory);
            if (formData.univIDo) setUnivIDo(formData.univIDo);
            if (formData.univWeDo) setUnivWeDo(formData.univWeDo);
            if (formData.univYouDo) setUnivYouDo(formData.univYouDo);
            if (formData.univDifferentiation) setUnivDifferentiation(formData.univDifferentiation);
            if (formData.univFormative) setUnivFormative(formData.univFormative);
            if (formData.univSummative) setUnivSummative(formData.univSummative);
            if (formData.univClosure) setUnivClosure(formData.univClosure);
          }
          cleanResponse = cleanResponse.replace(formMatch[0], '').trim() + "\n\n✅ **Form Auto-Filled successfully!** Please review the fields and click Save.";
        } catch (e) {
          console.error("Failed to parse AI JSON:", e);
        }
      }

      setAiMessages(prev => [...prev, { role: 'assistant', content: cleanResponse }]);
    } catch (e) {
      setAiMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
    } finally {
      setIsAiLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full pb-20"
    >
      {/* Back button — circular, matches AI Calculator style */}
      <button
        type="button"
        onClick={() => {
          const state = location.state as { from?: string } | null;
          const fromPath = state?.from;
          if (fromPath) {
            navigate(fromPath);
          } else {
            navigate(-1);
          }
        }}
        title="Go back"
        aria-label="Go back"
        className={`sticky top-0 z-20 self-start h-7 w-7 flex items-center justify-center rounded-full bg-zinc-100 dark:bg-white/5 text-zinc-500 hover:text-zinc-900 dark:hover:text-white border border-zinc-200 dark:border-white/10 transition-all duration-300 -ml-36 ml-4 mt-4 ${
          backVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-75 pointer-events-none'
        }`}
      >
        <ArrowLeft className="w-3.5 h-3.5" />
      </button>

      {/* Header */}
      <div className="px-4 md:px-12 pt-6 pb-4 border-b border-gray-100 dark:border-white/5">
        <div className="text-center mb-4">
          <h1 className="text-2xl font-extrabold tracking-tight">
            Lesson Planner
          </h1>
          <p className="text-muted-foreground text-[10px] font-black uppercase tracking-widest mt-1">{planType === 'cbc' ? 'CBC Curriculum (KICD Aligned)' : 'Universal Standard Template'}</p>
        </div>
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Plan Type</span>
            <Select value={planType} onValueChange={(v: 'universal' | 'cbc') => setPlanType(v)}>
              <SelectTrigger className="h-8 rounded-full border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-[10px] font-black uppercase tracking-widest px-3 gap-2 min-w-[180px]">
                <SelectValue placeholder="Select Plan" />
              </SelectTrigger>
              <SelectContent className="rounded-xl border-gray-200 dark:border-white/10 bg-white/95 dark:bg-[#0f172a]/95 backdrop-blur-xl shadow-2xl">
                <SelectItem value="universal" className="text-xs font-bold rounded-lg mb-1 focus:bg-indigo-50 dark:focus:bg-indigo-900/40 cursor-pointer py-2.5">
                  <div className="flex items-center gap-2">
                    <div className="h-5 w-5 rounded-full bg-indigo-500 text-white flex items-center justify-center text-[9px] font-black shrink-0">U</div>
                    <span>Universal Standard</span>
                  </div>
                </SelectItem>
                <SelectItem value="cbc" className="text-xs font-bold rounded-lg focus:bg-emerald-50 dark:focus:bg-emerald-900/40 cursor-pointer py-2.5">
                  <div className="flex items-center gap-2">
                    <div className="h-5 w-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[9px] font-black shrink-0">C</div>
                    <span>Kenya CBC (KICD)</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Scope</span>
            <Select value={lessonScope} onValueChange={(v: 'subject' | 'topic') => { setLessonScope(v); if (v === 'subject') setSelectedTopic(''); }}>
              <SelectTrigger className="h-8 rounded-full border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-[10px] font-black uppercase tracking-widest px-3 gap-2 min-w-[200px]">
                <SelectValue placeholder="Select Scope" />
              </SelectTrigger>
              <SelectContent className="rounded-xl border-gray-200 dark:border-white/10 bg-white/95 dark:bg-[#0f172a]/95 backdrop-blur-xl shadow-2xl">
                <SelectItem value="subject" className="text-xs font-bold rounded-lg mb-1 focus:bg-indigo-50 dark:focus:bg-indigo-900/40 cursor-pointer py-2.5">
                  <div className="flex items-center gap-2">
                    <div className="h-5 w-5 rounded-full bg-indigo-500 text-white flex items-center justify-center text-[9px] font-black shrink-0">S</div>
                    <span>Subject-Based (Whole Subject)</span>
                  </div>
                </SelectItem>
                <SelectItem value="topic" className="text-xs font-bold rounded-lg focus:bg-amber-50 dark:focus:bg-amber-900/40 cursor-pointer py-2.5">
                  <div className="flex items-center gap-2">
                    <div className="h-5 w-5 rounded-full bg-amber-500 text-white flex items-center justify-center text-[9px] font-black shrink-0">T</div>
                    <span>Topic / Subtopic-Based</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex gap-2">
             <Button variant="outline" type="button" onClick={() => {
               if (planType === 'cbc') {
                 setCbcStrand("Strand 1: Numbers");
                 setCbcSubStrand("Sub-strand 1.1: Addition");
                 setCbcKiq("• How do we add two-digit numbers?\n• Why is addition important in daily life?");
                 setCbcKnowledge("• Learners will understand the concept of carrying over.");
                 setCbcSkill("• Learners will be able to perform vertical addition.");
                 setCbcAttitude("• Learners will appreciate accuracy in calculation.");
                 setCbcTextbook("• Primary Mathematics Grade 4, Pg 22");
                 setCbcRealia("• Counting blocks\n• Bottle tops");
                 setCbcDigital("• Math Quiz App on tablet");
                 setCbcIntro("• Sing a number song.\n• Review adding single digits.");
                 setCbcDevelopment("• Demonstrate adding two-digit numbers without carrying.\n• Introduce carrying over using blocks.\n• Have students practice in pairs.");
                 setCbcConclusionPhase("• Review the main steps on the board.\n• Ask random students to solve a problem.");
                 setCbcCompetencies("• Communication and Collaboration\n• Critical Thinking");
                 setCbcPci("• Financial literacy (using money examples)");
                 setCbcValues("• Responsibility\n• Integrity");
                 setCbcAssessmentMethod("• Observation\n• Written quiz");
                 setCbcRubric("• Exceeds Expectations: Solves all correctly without help.\n• Meets Expectations: Solves most correctly.\n• Below Expectations: Needs help with basic addition.");
                 setCbcDifferentiation("• Use larger blocks for visual learners.\n• Pair struggling students with peer tutors.");
               } else {
                 setOutcomes("• Students will be able to add fractions with like denominators.\n• Students will demonstrate understanding of the numerator and denominator.");
                 setUnivMaterials("• Whiteboard\n• Fraction tiles\n• Worksheets");
                 setUnivAnticipatory("• Start with a pizza slicing example on the board.");
                 setUnivIDo("• Demonstrate adding 1/4 + 2/4 using visual fraction models.");
                 setUnivWeDo("• Solve 2/8 + 3/8 together as a class using mini-whiteboards.");
                 setUnivYouDo("• Students complete 5 practice problems independently.");
                 setUnivDifferentiation("• Provide fraction blocks for struggling learners.\n• Give advanced students mixed numbers.");
                 setUnivFormative("• Observe students during We Do and check whiteboards.");
                 setUnivSummative("• Exit ticket with 2 addition problems.");
                 setUnivClosure("• Review the exit ticket and ask one student to summarize the rule for adding like denominators.");
               }
               toast({ title: "Auto-filled", description: "Test data populated." });
             }} className="rounded-full text-xs h-9 border-gray-200">
               Auto-fill (Test)
             </Button>

             <Button variant="ghost" onClick={() => setAiPanelOpen(true)} className="rounded-full text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-950/30">
               <Sparkles className="mr-2 h-4 w-4" /> AI Assist
             </Button>
             <Button onClick={handleSave} disabled={isSaving} className="rounded-full px-6 bg-blue-600 hover:bg-blue-700 text-white shadow-lg transition-all">
               {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
               {isSaving ? 'Saving...' : 'Save'}
             </Button>
          </div>
        </div>
      </div>

      <div className="px-4 md:px-12 pt-8 max-w-7xl mx-auto">
        {/* Main Form Area */}
        <div className="w-full space-y-8">
          
          <div className="bg-white dark:bg-[#020617] p-8 rounded-3xl border border-gray-100 dark:border-white/5 shadow-sm">
             {/* Subject (read-only — comes from course context) */}
             <div className="mb-6 flex items-center gap-3 flex-wrap">
                <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Subject</span>
                <div className="inline-flex items-center gap-2 h-8 px-3 rounded-full bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20">
                  <BookOpen className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                  <span className="text-xs font-bold text-indigo-700 dark:text-indigo-300">{subjectName || 'No subject selected'}</span>
                </div>
                {lessonState?.subjectId && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsDeleteDialogOpen(true)}
                    title="Delete subject"
                    aria-label="Delete subject"
                    className="h-8 w-8 rounded-full text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 hover:text-rose-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
             </div>

             {/* Lesson Metadata — Time Limit & (optional) Topic depending on scope */}
             <div className={`grid grid-cols-1 ${lessonScope === 'topic' ? 'md:grid-cols-2' : ''} gap-4 mb-6 border-b border-gray-100 dark:border-white/5 pb-6`}>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 block mb-1">Time Limit</label>
                  <Input defaultValue="40 mins" className="h-8 text-xs font-bold bg-gray-50 dark:bg-white/5 border-none" />
                </div>
                {lessonScope === 'topic' && (
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 block mb-1">Topic / Subtopic</label>
                    {syllabusTopics.length > 0 ? (
                      <Select value={selectedTopic} onValueChange={setSelectedTopic}>
                        <SelectTrigger className="h-8 text-xs font-bold bg-gray-50 dark:bg-white/5 border-none">
                          <SelectValue placeholder="Select a topic or subtopic" />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl border-gray-200 dark:border-white/10 bg-white/95 dark:bg-[#0f172a]/95 backdrop-blur-xl shadow-2xl">
                          {syllabusTopics.map((topic) => (
                            <SelectGroup key={topic.id}>
                              <SelectLabel className="text-[10px] font-black uppercase tracking-widest text-gray-400 px-2 py-1.5">
                                {topic.title}
                              </SelectLabel>
                              <SelectItem value={topic.id} className="text-xs font-medium rounded-lg cursor-pointer pl-6">
                                {topic.title}
                              </SelectItem>
                              {topic.subTopics?.map((st) => (
                                <SelectItem key={st.id} value={`${topic.id}__${st.id}`} className="text-xs font-medium rounded-lg cursor-pointer pl-10 text-gray-600 dark:text-gray-400">
                                  ↳ {st.title}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input placeholder="No topics found in syllabus — add them in the syllabus editor" disabled className="h-8 text-xs font-bold bg-gray-50 dark:bg-white/5 border-none opacity-60" />
                    )}
                  </div>
                )}
             </div>

             <input 
              type="text" 
              placeholder="Lesson Title..."
              className="text-4xl font-bold w-full bg-transparent border-none outline-none placeholder:text-gray-200 dark:placeholder:text-gray-800 tracking-tighter mb-8"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />

            {planType === 'cbc' ? (
              <>
                {/* CBC Section 1: Strand & Sub-Strand with KIQ */}
                <div className="space-y-4 mb-8">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 rounded-md">1</Badge>
                    <h3 className="font-bold text-lg">Strand & Sub-Strand</h3>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">Extracted word-for-word from the KICD curriculum design document.</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase tracking-widest text-gray-500">Strand</label>
                      <Input value={cbcStrand} onChange={(e) => setCbcStrand(e.target.value)} placeholder="e.g. Numbers" className="h-9 text-xs font-medium bg-gray-50/50 dark:bg-white/5 border-gray-200 dark:border-white/10" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase tracking-widest text-gray-500">Sub-Strand</label>
                      <Input value={cbcSubStrand} onChange={(e) => setCbcSubStrand(e.target.value)} placeholder="e.g. Whole Numbers" className="h-9 text-xs font-medium bg-gray-50/50 dark:bg-white/5 border-gray-200 dark:border-white/10" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-500">Key Inquiry Question (KIQ)</label>
                    <BulletTextarea value={cbcKiq} onChange={setCbcKiq}
                      placeholder="A single open-ended real-life anchor question connecting to learners' everyday lives..."
                      className="min-h-[80px] bg-gray-50/50 dark:bg-white/5 border-gray-200 dark:border-white/10 focus-visible:ring-emerald-500 font-medium" />
                  </div>
                </div>

                {/* CBC Section 2: Specific Learning Outcomes (3 domains) */}
                <div className="space-y-4 mb-8">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 rounded-md">2</Badge>
                    <h3 className="font-bold text-lg">Specific Learning Outcomes</h3>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">CBC requires a holistic approach covering all three domains.</p>
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase tracking-widest text-blue-600">Knowledge (Cognitive) — what to know</label>
                      <BulletTextarea value={cbcKnowledge} onChange={setCbcKnowledge} placeholder="What the learner should know or understand..." className="min-h-[80px] bg-blue-50/30 dark:bg-blue-900/10 border-blue-100 dark:border-blue-900/30 focus-visible:ring-blue-500" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase tracking-widest text-emerald-600">Skill (Psychomotor) — what to do</label>
                      <BulletTextarea value={cbcSkill} onChange={setCbcSkill} placeholder="What the learner should be able to do..." className="min-h-[80px] bg-emerald-50/30 dark:bg-emerald-900/10 border-emerald-100 dark:border-emerald-900/30 focus-visible:ring-emerald-500" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase tracking-widest text-rose-600">Attitude (Affective) — values & feelings</label>
                      <BulletTextarea value={cbcAttitude} onChange={setCbcAttitude} placeholder="Values or feelings the learner should develop..." className="min-h-[80px] bg-rose-50/30 dark:bg-rose-900/10 border-rose-100 dark:border-rose-900/30 focus-visible:ring-rose-500" />
                    </div>
                  </div>
                </div>

                {/* CBC Section 3: Learning Resources */}
                <div className="space-y-4 mb-8">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 rounded-md">3</Badge>
                    <h3 className="font-bold text-lg">Learning Resources</h3>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">CBC emphasizes using locally available, improvised materials.</p>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase tracking-widest text-gray-500">Textbook Reference</label>
                      <BulletTextarea value={cbcTextbook} onChange={setCbcTextbook} placeholder="Title, author, page numbers..." className="min-h-[80px] bg-gray-50/50 dark:bg-white/5 border-gray-200 dark:border-white/10" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase tracking-widest text-gray-500">Realia & Local Materials</label>
                      <BulletTextarea value={cbcRealia} onChange={setCbcRealia} placeholder="Locally available materials..." className="min-h-[80px] bg-gray-50/50 dark:bg-white/5 border-gray-200 dark:border-white/10" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase tracking-widest text-gray-500">Digital Devices</label>
                      <BulletTextarea value={cbcDigital} onChange={setCbcDigital} placeholder="ICT integration (where applicable)..." className="min-h-[80px] bg-gray-50/50 dark:bg-white/5 border-gray-200 dark:border-white/10" />
                    </div>
                  </div>
                </div>

                {/* CBC Section 4: Organisation of Learning — 3 Phases */}
                <div className="space-y-4 mb-8">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 rounded-md">4</Badge>
                    <h3 className="font-bold text-lg">Organisation of Learning</h3>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">Both Teacher and Learner activities are explicitly stated.</p>
                  <div className="space-y-4">
                    <div className="rounded-xl border border-emerald-100 dark:border-emerald-900/30 bg-emerald-50/20 dark:bg-emerald-900/5 p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Phase 1 — Introduction</span>
                        <span className="text-[10px] text-gray-400">~5–10 mins</span>
                      </div>
                      <BulletTextarea value={cbcIntro} onChange={setCbcIntro} placeholder="Activating prior knowledge, reviewing previous lessons, posing the KIQ..." className="min-h-[100px] bg-white/60 dark:bg-black/20 border-emerald-100 dark:border-emerald-900/30" />
                    </div>
                    <div className="rounded-xl border border-emerald-100 dark:border-emerald-900/30 bg-emerald-50/20 dark:bg-emerald-900/5 p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Phase 2 — Lesson Development</span>
                        <span className="text-[10px] text-gray-400">~20–25 mins</span>
                      </div>
                      <BulletTextarea value={cbcDevelopment} onChange={setCbcDevelopment} placeholder="Core exploratory phase. Emphasize learner-centered activities (group work, experiments, discussions). Teacher as facilitator..." className="min-h-[120px] bg-white/60 dark:bg-black/20 border-emerald-100 dark:border-emerald-900/30" />
                    </div>
                    <div className="rounded-xl border border-emerald-100 dark:border-emerald-900/30 bg-emerald-50/20 dark:bg-emerald-900/5 p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Phase 3 — Conclusion</span>
                        <span className="text-[10px] text-gray-400">~5 mins</span>
                      </div>
                      <BulletTextarea value={cbcConclusionPhase} onChange={setCbcConclusionPhase} placeholder="Summarizing key points, answering the KIQ, follow-up tasks..." className="min-h-[100px] bg-white/60 dark:bg-black/20 border-emerald-100 dark:border-emerald-900/30" />
                    </div>
                  </div>
                </div>

                {/* CBC Section 5: Core Competencies, PCIs & Values */}
                <div className="space-y-4 mb-8">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 rounded-md">5</Badge>
                    <h3 className="font-bold text-lg">Core Competencies, PCIs & Values</h3>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">Link to the 7 KICD core competencies and integrate pertinent issues.</p>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase tracking-widest text-gray-500">Core Competencies</label>
                      <BulletTextarea value={cbcCompetencies} onChange={setCbcCompetencies} placeholder="e.g. Communication & Collaboration, Critical Thinking..." className="min-h-[100px] bg-gray-50/50 dark:bg-white/5 border-gray-200 dark:border-white/10" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase tracking-widest text-gray-500">Pertinent & Contemporary Issues (PCIs)</label>
                      <BulletTextarea value={cbcPci} onChange={setCbcPci} placeholder="e.g. Environmental awareness, health/hygiene, peace education..." className="min-h-[100px] bg-gray-50/50 dark:bg-white/5 border-gray-200 dark:border-white/10" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase tracking-widest text-gray-500">Values</label>
                      <BulletTextarea value={cbcValues} onChange={setCbcValues} placeholder="e.g. Respect, responsibility, honesty..." className="min-h-[100px] bg-gray-50/50 dark:bg-white/5 border-gray-200 dark:border-white/10" />
                    </div>
                  </div>
                </div>

                {/* CBC Section 6: Assessment & Rubrics + Differentiation */}
                <div className="space-y-4 mb-8">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 rounded-md">6</Badge>
                    <h3 className="font-bold text-lg">Assessment & Rubrics</h3>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">Clear benchmarks for Exceeding, Meeting, Approaching, or Below Expectations.</p>
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase tracking-widest text-gray-500">Assessment Method & Tool</label>
                      <Input value={cbcAssessmentMethod} onChange={(e) => setCbcAssessmentMethod(e.target.value)} placeholder="e.g. Observation schedule, oral questioning, checklist, portfolio" className="h-9 text-xs font-medium bg-gray-50/50 dark:bg-white/5 border-gray-200 dark:border-white/10" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase tracking-widest text-gray-500">Rubric / Criteria</label>
                      <BulletTextarea value={cbcRubric} onChange={setCbcRubric} placeholder="Exceeding / Meeting / Approaching / Below Expectations..." className="min-h-[100px] bg-gray-50/50 dark:bg-white/5 border-gray-200 dark:border-white/10" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase tracking-widest text-gray-500">Differentiated Learning</label>
                      <BulletTextarea value={cbcDifferentiation} onChange={setCbcDifferentiation} placeholder="How will you support learners with special needs, slow learners, or gifted learners?" className="min-h-[100px] bg-gray-50/50 dark:bg-white/5 border-gray-200 dark:border-white/10" />
                    </div>
                  </div>
                </div>

                {/* CBC Section 7: Reflection (Post-Lesson) */}
                <div className="space-y-4 mb-8">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-yellow-50 text-yellow-700 border-yellow-200 rounded-md">7</Badge>
                    <h3 className="font-bold text-lg">Reflection (Post-Lesson)</h3>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">Filled immediately after the lesson is taught.</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase tracking-widest text-emerald-600">What went well?</label>
                      <BulletTextarea value={cbcWhatWentWell} onChange={setCbcWhatWentWell} placeholder="Successes in achieving the learning outcomes..." className="min-h-[100px] bg-emerald-50/30 dark:bg-emerald-900/10 border-emerald-100 dark:border-emerald-900/30" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase tracking-widest text-rose-600">What didn't go well?</label>
                      <BulletTextarea value={cbcWhatDidnt} onChange={setCbcWhatDidnt} placeholder="Challenges faced..." className="min-h-[100px] bg-rose-50/30 dark:bg-rose-900/10 border-rose-100 dark:border-rose-900/30" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase tracking-widest text-amber-600">Follow-up actions</label>
                      <BulletTextarea value={cbcFollowUp} onChange={setCbcFollowUp} placeholder="How to remedy the challenges (e.g. remedial teaching)..." className="min-h-[100px] bg-amber-50/30 dark:bg-amber-900/10 border-amber-100 dark:border-amber-900/30" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase tracking-widest text-indigo-600">Remarks (QASO / Supervisor)</label>
                      <BulletTextarea value={cbcRemarks} onChange={setCbcRemarks} placeholder="Observations from supervisors or Quality Assurance Officers..." className="min-h-[100px] bg-indigo-50/30 dark:bg-indigo-900/10 border-indigo-100 dark:border-indigo-900/30" />
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
            {/* Section 1: Learning Objectives */}
            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-100 rounded-md">1</Badge>
                <h3 className="font-bold text-lg">Learning Objectives</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-2">SMART objectives — what will students know or be able to do by the end of the lesson?</p>
              <BulletTextarea
                value={outcomes}
                onChange={setOutcomes}
                placeholder="List clear, measurable outcomes (e.g., SWBAT...)"
                className="min-h-[120px] bg-gray-50/50 dark:bg-white/5 border-gray-200 dark:border-white/10 focus-visible:ring-blue-500 font-medium"
              />
            </div>

            {/* Section 2: Materials and Resources */}
            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-100 rounded-md">2</Badge>
                <h3 className="font-bold text-lg">Materials and Resources</h3>
              </div>
              <BulletTextarea value={univMaterials} onChange={setUnivMaterials}
                placeholder="Textbooks, handouts, multimedia presentations, lab equipment, art supplies, specific software..."
                className="min-h-[100px] bg-gray-50/50 dark:bg-white/5 border-gray-200 dark:border-white/10 focus-visible:ring-blue-500" />
            </div>

            {/* Section 3: Anticipatory Set (The Hook / Warm-Up) */}
            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-100 rounded-md">3</Badge>
                <h3 className="font-bold text-lg">Anticipatory Set (The Hook)</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-2">Connects past learning to the new topic and activates prior knowledge.</p>
              <BulletTextarea value={univAnticipatory} onChange={setUnivAnticipatory}
                placeholder="A brief activity or discussion designed to grab the students' attention at the start of the lesson..."
                className="min-h-[100px] bg-gray-50/50 dark:bg-white/5 border-gray-200 dark:border-white/10 focus-visible:ring-blue-500" />
            </div>

            {/* Section 4: Instructional Procedure — Gradual Release of Responsibility */}
            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-100 rounded-md">4</Badge>
                <h3 className="font-bold text-lg">Instructional Procedure</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-2">Gradual Release of Responsibility — I Do / We Do / You Do.</p>
              <div className="space-y-3">
                <div className="rounded-xl border border-indigo-100 dark:border-indigo-900/30 bg-indigo-50/20 dark:bg-indigo-900/5 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] font-black uppercase tracking-widest text-indigo-700">I Do — Direct Instruction</span>
                  </div>
                  <BulletTextarea value={univIDo} onChange={setUnivIDo} placeholder="Teacher introduces new concepts, models skills, provides information..." className="min-h-[100px] bg-white/60 dark:bg-black/20 border-indigo-100 dark:border-indigo-900/30" />
                </div>
                <div className="rounded-xl border border-indigo-100 dark:border-indigo-900/30 bg-indigo-50/20 dark:bg-indigo-900/5 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] font-black uppercase tracking-widest text-indigo-700">We Do — Guided Practice</span>
                  </div>
                  <BulletTextarea value={univWeDo} onChange={setUnivWeDo} placeholder="Teacher and students work through examples together. Immediate feedback and support..." className="min-h-[100px] bg-white/60 dark:bg-black/20 border-indigo-100 dark:border-indigo-900/30" />
                </div>
                <div className="rounded-xl border border-indigo-100 dark:border-indigo-900/30 bg-indigo-50/20 dark:bg-indigo-900/5 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] font-black uppercase tracking-widest text-indigo-700">You Do — Independent Practice</span>
                  </div>
                  <BulletTextarea value={univYouDo} onChange={setUnivYouDo} placeholder="Students apply what they've learned on their own or in small groups..." className="min-h-[100px] bg-white/60 dark:bg-black/20 border-indigo-100 dark:border-indigo-900/30" />
                </div>
              </div>
            </div>

            {/* Section 5: Differentiation / Accommodations */}
            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-100 rounded-md">5</Badge>
                <h3 className="font-bold text-lg">Differentiation / Accommodations</h3>
              </div>
              <BulletTextarea value={univDifferentiation} onChange={setUnivDifferentiation}
                placeholder="Strategies to adjust the lesson to meet the diverse needs of all learners (modifications for students with disabilities, ELLs, advanced learners)..."
                className="min-h-[100px] bg-gray-50/50 dark:bg-white/5 border-gray-200 dark:border-white/10 focus-visible:ring-blue-500" />
            </div>

            {/* Section 6: Assessment / Evaluation */}
            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-100 rounded-md">6</Badge>
                <h3 className="font-bold text-lg">Assessment / Evaluation</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-2">How will the teacher know if the students have met the objectives?</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase tracking-widest text-gray-500">Formative</label>
                  <BulletTextarea value={univFormative} onChange={setUnivFormative} placeholder="Ongoing checks: questioning, thumbs up/down, exit tickets..." className="min-h-[100px] bg-gray-50/50 dark:bg-white/5 border-gray-200 dark:border-white/10" />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase tracking-widest text-gray-500">Summative</label>
                  <BulletTextarea value={univSummative} onChange={setUnivSummative} placeholder="Formal evaluation: quiz, project, written response..." className="min-h-[100px] bg-gray-50/50 dark:bg-white/5 border-gray-200 dark:border-white/10" />
                </div>
              </div>
            </div>

            {/* Section 7: Lesson Closure (Plenary) */}
            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-100 rounded-md">7</Badge>
                <h3 className="font-bold text-lg">Lesson Closure (Plenary)</h3>
              </div>
              <BulletTextarea value={univClosure} onChange={setUnivClosure}
                placeholder="Summarize main points, review objectives, allow students to reflect on what they've learned..."
                className="min-h-[100px] bg-gray-50/50 dark:bg-white/5 border-gray-200 dark:border-white/10 focus-visible:ring-blue-500" />
            </div>

            {/* Section 8: Teacher Reflection */}
            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-yellow-50 text-yellow-700 border-yellow-200 rounded-md">8</Badge>
                <h3 className="font-bold text-lg">Teacher Reflection</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-2">Completed after the lesson is taught.</p>
              <BulletTextarea value={reflection} onChange={setReflection}
                placeholder="Did the students meet the objectives? What activities were most effective? What challenges arose, and what needs to be changed for future lessons?"
                className="min-h-[100px] bg-yellow-50/50 dark:bg-yellow-900/10 border-yellow-200 dark:border-yellow-900/30 focus-visible:ring-yellow-500" />
            </div>
              </>
            )}

          </div>
        </div>
      </div>

      {/* Delete Subject Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="w-[calc(100%-2rem)] bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-900 dark:text-white">Delete Subject?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500 dark:text-slate-400">
              This will delete the whole subject/lecture along with all the notes and references. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSubject}
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Proceed"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AI Assist Slide-out Panel */}
      <AnimatePresence>
        {aiPanelOpen && (
          <motion.div
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-0 md:inset-y-0 md:right-0 md:left-auto md:w-[400px] h-[100dvh] md:h-screen bg-white dark:bg-[#0f172a] shadow-2xl border-l border-gray-100 dark:border-white/10 z-50 flex flex-col"
          >
            <div className="flex items-center justify-between px-4 py-3 sm:py-4 border-b border-gray-100 dark:border-white/5" style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}>
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <h3 className="text-sm font-bold">AI Lesson Assistant</h3>
                  <p className="text-[10px] text-muted-foreground">Chat to auto-fill the plan</p>
                </div>
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={() => setAiPanelOpen(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4 overscroll-contain" id="ai-chat-messages">
              {aiMessages.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <div className="mx-auto w-12 h-12 bg-indigo-50 dark:bg-indigo-900/20 rounded-full flex items-center justify-center mb-4">
                    <Bot className="w-6 h-6 text-indigo-500" />
                  </div>
                  <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-2">Ready to help!</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Tell me what lesson you want to teach, and I'll ask a few questions to write your full {planType === 'cbc' ? 'CBC' : 'Universal'} plan.
                  </p>
                </div>
              ) : (
                aiMessages.map((m, idx) => (
                  <div key={idx} className={`flex gap-3 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${m.role === 'user' ? 'bg-blue-100 text-blue-600' : 'bg-indigo-100 text-indigo-600'}`}>
                      {m.role === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                    </div>
                    <div className={`px-4 py-3 rounded-2xl text-sm max-w-[85%] sm:max-w-[80%] whitespace-pre-wrap break-words overflow-hidden ${m.role === 'user' ? 'bg-blue-600 text-white rounded-tr-none' : 'bg-gray-100 dark:bg-white/5 text-gray-800 dark:text-gray-200 rounded-tl-none'}`}>
                      {m.content}
                    </div>
                  </div>
                ))
              )}
              {isAiLoading && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                    <Bot className="w-4 h-4 text-indigo-600" />
                  </div>
                  <div className="px-4 py-3 rounded-2xl bg-gray-100 dark:bg-white/5 rounded-tl-none flex items-center">
                    <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
                  </div>
                </div>
              )}
              <div ref={aiScrollRef} />
            </div>

            <div className="p-3 sm:p-4 border-t border-gray-100 dark:border-white/5 bg-gray-50/50 dark:bg-black/20" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
              <div className="relative">
                <textarea 
                  ref={aiInputRef}
                  value={aiInput}
                  onChange={(e) => {
                    setAiInput(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendAiMessage(); } }}
                  placeholder="E.g., I want to teach fractions to grade 4..."
                  rows={1}
                  className="w-full rounded-2xl bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 pr-12 pl-4 py-3 text-sm min-h-[44px] max-h-[120px] resize-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:outline-none transition-all"
                  style={{ height: 'auto' }}
                />
                <Button 
                  size="icon" 
                  className="absolute bottom-2 right-2 h-10 w-10 sm:h-8 sm:w-8 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white active:scale-95 transition-transform"
                  onClick={handleSendAiMessage}
                  disabled={isAiLoading || !aiInput.trim()}
                >
                  <Send className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </motion.div>
  );
};

export default LessonCreateView;


